import type { TFunction } from "i18next";
import { useRef, type RefObject } from "react";
import U2000SaveCancelDialog from "../../../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000InputText from "../../../../../u2000Core/components/formControls/input/U2000InputText";
import U2000CheckBoxArray from "../../../../../u2000Core/components/formControls/checkbox/U2000CheckBoxArray";
import type { U2101LogicalUnitActionHandler } from "./U2101LogicalUnitActionHandlers";
import { LOGICAL_UNIT_TYPES, type LogicalUnitType } from "../../../common/logicalUnitTypes";

// Component props interface
interface U2101LogicalUnitActionDlgProps {
  u2101LogicalUnitActionHandler: U2101LogicalUnitActionHandler;
  t: TFunction;
  NS: string;
}

// Dialog component for editing logical unit actions
function U2101LogicalUnitActionEditDlg({ u2101LogicalUnitActionHandler, t, NS }: U2101LogicalUnitActionDlgProps) {
  const { formStateHandlers } = u2101LogicalUnitActionHandler;

  // Ref for the code action input to be used as default focus
  const codeActionRef = useRef<HTMLInputElement>(null);

  // Unified handler that manages Save and Cancel differently
  const handleClose = (action: "save" | "cancel") => {
    if (action === "cancel") {
      // Temporarily disable onBlur validation to prevent unwanted validation on cancel
      const originalOnBlur = formStateHandlers.onBlur;
      formStateHandlers.onBlur = () => {
        /* Ignore onBlur during cancel */
      };

      // Close dialog immediately without validation
      u2101LogicalUnitActionHandler.onDlgClose("cancel");

      // Restore original onBlur handler after dialog closes
      setTimeout(() => {
        formStateHandlers.onBlur = originalOnBlur;
      }, 0);
    } else if (action === "save") {
      // Normal save flow with validation
      u2101LogicalUnitActionHandler.onDlgClose("save");
    }
  };

  // render
  return (
    <U2000SaveCancelDialog
      isOpen={u2101LogicalUnitActionHandler.isDialogOpen}
      onClose={handleClose}
      title={t("lblLogicalUnitAction", { ns: NS })}
      isValid={true}
      defaultFocusRef={codeActionRef as RefObject<HTMLElement>}
    >
      <div className="flex flex-col gap-y-4">
        <div>
          <U2000InputText
            fieldName="logicalUnitActionCode"
            label={t("lblLogicalUnitActionCode", { ns: NS })}
            formStateHandlers={formStateHandlers}
            inputRef={codeActionRef as React.RefObject<HTMLInputElement>}
          />
        </div>
        <div>
          <U2000InputText fieldName="logicalUnitActionDescription" label={t("lblLogicalUnitActionDescription", { ns: NS })} formStateHandlers={formStateHandlers} />
        </div>
        <div className="col-span-2 mt-2 flex items-center gap-8">
          {LOGICAL_UNIT_TYPES.map((type: LogicalUnitType) => (
            <U2000CheckBoxArray
              formStateHandlers={formStateHandlers}
              key={`logical-unit-action-type-${type.value}`}
              fieldName="logicalUnitActionTypes"
              value={type.value}
              label={typeof type.label === "object" ? type.label["fr"] : String(type.label)}
              readonly={false}
            />
          ))}
        </div>
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U2101LogicalUnitActionEditDlg;
